/**
 * MenuRecommendationsCard (web) — Dining Concierge ranked picks. Composes the
 * established theme primitives (Card / Text) — no bespoke surfaces or colors.
 */
import Card from '../../common/Card';
import Text from '../../common/Text';
import { useLanguage } from '../../hooks/useLanguage';

interface DishPick {
  name?: string;
  name_original?: string;
  price?: number | null;
  currency?: string;
  reason?: string;
  tags?: string[];
}

function priceLabel(price?: number | null, currency?: string): string | null {
  if (price === null || price === undefined) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(price);
  } catch {
    return `${price} ${currency || 'USD'}`;
  }
}

export default function MenuRecommendationsCard({ item }: { item: any }) {
  const { t } = useLanguage();
  const card = item?.dishes
    ? item
    : item?.data?.dishes
      ? item.data
      : item || {};
  const dishes: DishPick[] = card.dishes || [];
  const skip: Array<{ name?: string; reason?: string }> = card.skip || [];
  if (!dishes.length && !skip.length) return null;

  return (
    <Card>
      <Text variant="h3">
        {card.restaurant?.name || t('MENU_RECOMMENDATIONS_TITLE')}
      </Text>
      {!!card.restaurant?.cuisines?.length && (
        <Text variant="caption">{card.restaurant.cuisines.join(' · ')}</Text>
      )}
      {dishes.map((d, i) => {
        const price = priceLabel(d.price, d.currency);
        return (
          <div key={`pick-${i}`} style={ROW}>
            <Text variant="label">{i + 1}</Text>
            <div style={{ flex: 1 }}>
              <div style={ROW_BETWEEN}>
                <Text variant="body">{d.name}</Text>
                {!!price && <Text variant="label">{price}</Text>}
              </div>
              {!!d.name_original && d.name_original !== d.name && (
                <Text variant="caption">{d.name_original}</Text>
              )}
              {!!d.reason && <Text variant="label">{d.reason}</Text>}
              {!!d.tags?.length && (
                <div style={TAGS}>
                  {d.tags.slice(0, 4).map((tg, ti) => (
                    <Text key={ti} variant="pill">
                      {tg}
                    </Text>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {!!skip.length && (
        <div style={{ marginTop: 8 }}>
          <Text variant="label">{t('MENU_SKIP_LABEL')}</Text>
          {skip.map((s, i) => (
            <Text key={`s-${i}`} variant="caption">
              • {s.name}
              {s.reason ? ` — ${s.reason}` : ''}
            </Text>
          ))}
        </div>
      )}
      {!!card.attribution?.text && (
        <Text variant="caption">{card.attribution.text}</Text>
      )}
    </Card>
  );
}

const ROW: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  alignItems: 'flex-start',
  padding: '6px 0',
};
const ROW_BETWEEN: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
};
const TAGS: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginTop: 4,
};
